import {ImageCodecFailure} from '../core/image-codec';

type HeifImage = import('libheif-js/libheif-wasm/libheif-bundle.mjs').HeifImage;
type LibheifModule = import('libheif-js/libheif-wasm/libheif-bundle.mjs').LibheifModule;

let libheifPromise: Promise<LibheifModule> | null = null;

/** Loaded on first use only: the WASM bundle is ~2 MB and most uploads never need it. */
const loadLibheif = (): Promise<LibheifModule> => {
  libheifPromise ??= import('libheif-js/libheif-wasm/libheif-bundle.mjs').then(async loaded => {
    const exported = loaded.default;
    return typeof exported === 'function' ? await exported() : exported;
  });
  return libheifPromise;
};

const decodeFailure = (message: string, cause?: unknown): ImageCodecFailure =>
  new ImageCodecFailure('decode-failed', message, {cause});

const renderPrimary = (image: HeifImage): Promise<ImageData> =>
  new Promise((resolve, reject) => {
    const width = image.get_width();
    const height = image.get_height();
    if (width <= 0 || height <= 0) {
      reject(decodeFailure('HEIC image has no pixels.'));
      return;
    }
    image.display(new ImageData(width, height), result => {
      if (result === null) {
        reject(decodeFailure('HEIC image could not be rendered.'));
        return;
      }
      resolve(result);
    });
  });

/**
 * Decodes a HEIC/HEIF (or AVIF, when the build supports it) to raw RGBA so the
 * pipeline encodes it exactly once, instead of converting to JPEG first.
 */
export const decodeHeicToImageData = async (blob: Blob): Promise<ImageData> => {
  const libheif = await loadLibheif().catch(cause => {
    throw decodeFailure('HEIC decoder could not be loaded.', cause);
  });
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let images: HeifImage[];
  try {
    images = new libheif.HeifDecoder().decode(bytes);
  } catch (cause) {
    throw decodeFailure('HEIC image could not be decoded.', cause);
  }
  const primary = images[0];
  if (primary === undefined) {
    throw decodeFailure('HEIC file contains no image.');
  }
  try {
    return await renderPrimary(primary);
  } finally {
    for (const image of images) {
      image.free();
    }
  }
};
